<template>
  <v-app>
    <div class="taxonomy-editor-container">
      <div class="flex-container">
        <!-- SIDEBAR -->
        <div class="sidebar">
          <div class="search-container">
            <!-- This search box controls "searchQuery" for other views -->
            <v-text-field
              v-model="searchQuery"
              label="Search attribute keys or values..."
              append-icon="mdi-magnify"
              @keyup.enter="onSearch"
              clearable
              hide-details
            ></v-text-field>
          </div>

          <div class="view-switcher">
            <!-- 1) Taxonomy Editor -->
            <v-btn
              @click="currentView = 'attributeManagement'"
              :class="{ active: currentView === 'attributeManagement' }"
              depressed
            >
              Taxonomy Editor
            </v-btn>

            <!-- 2) Attribute Overview -->
            <v-btn
              @click="currentView = 'attributeOverview'"
              :class="{ active: currentView === 'attributeOverview' }"
              depressed
            >
              Attribute Overview
            </v-btn>

            <!-- 3) Attribute Editor -->
            <v-btn
              @click="currentView = 'attributeEditor'"
              :class="{ active: currentView === 'attributeEditor' }"
              depressed
            >
              Attribute Editor
            </v-btn>

            <!-- 4) Language Variants Editor -->
            <v-btn
              @click="currentView = 'languageVariants'"
              :class="{ active: currentView === 'languageVariants' }"
              depressed
            >
              Edit Language Variants
            </v-btn>
          </div>

          <div class="bottom-controls">
            <!-- Load Version and Load Taxonomy group -->
            <div style="display: flex; align-items: center; gap: 0.5rem">
              <v-autocomplete
                v-model="loadVersion"
                :items="formattedVersions"
                label="Load Version"
                dense
                hide-details
                clearable
                placeholder="Type to search versions"
                style="flex: 1"
                @change="onLoadVersionChange"
              ></v-autocomplete>
              <v-btn icon @click="loadTaxonomyVersions">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
            <div
              style="
                display: flex;
                align-items: center;
                margin-top: 4px;
                margin-bottom: 4px;
              "
            >
              <v-switch
                v-model="showBackupVersions"
                label="Show backup versions"
                dense
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
            </div>

            <v-btn
              @click="loadTaxonomyFromCloud"
              class="primary-button mt-2"
              color="primary"
            >
              Load Taxonomy
            </v-btn>

            <!-- SAVE AS VERSION -->
            <v-text-field
              v-model="saveVersion"
              label="Save As Version"
              placeholder="(Optional) Enter new version name"
              hide-details
              dense
              style="max-width: 200px; margin-bottom: 0.5rem"
            />
            <!-- VERSION DESCRIPTION -->
            <v-text-field
              v-model="versionDescription"
              label="Version Description"
              placeholder="Brief description of changes"
              hide-details
              dense
              style="max-width: 200px; margin-bottom: 0.5rem"
            />
            <div class="mt-2">
              <v-textarea
                v-model="currentVersionDescription"
                label="Current Version Description"
                readonly
                rows="2"
                hide-details
              ></v-textarea>
            </div>

            <div class="d-flex flex-column mt-2">
              <!-- Version Loaded indicator -->
              <v-chip
                :color="
                  loadedVersion
                    ? loadedVersion.toLowerCase() === 'production'
                      ? 'success'
                      : 'primary'
                    : 'grey'
                "
                small
                outlined
              >
                Version Loaded: {{ loadedVersion ? loadedVersion : "None" }}
              </v-chip>
              <!-- Status chip -->
              <v-chip
                :color="hasUnsavedChanges ? 'warning' : 'success'"
                small
                outlined
                class="ml-2"
              >
                {{
                  hasUnsavedChanges ? "Unsaved Changes" : "All Changes Saved"
                }}
              </v-chip>
            </div>

            <!-- SAVE CHANGES -->
            <v-btn
              @click="saveChanges"
              class="primary-button"
              color="primary"
              style="margin-bottom: 0.5rem"
            >
              Save Changes
            </v-btn>

            <!-- DOWNLOAD TAXONOMY -->
            <v-btn
              @click="downloadTaxonomy"
              class="primary-button"
              color="primary"
              style="margin-bottom: 0.5rem"
            >
              Download Taxonomy
            </v-btn>
            <!-- Publish Taxonomy button -->
            <v-btn
              @click="openPublishDialog"
              class="primary-button"
              color="primary"
              style="margin-bottom: 0.5rem"
            >
              Publish Taxonomy
            </v-btn>
          </div>
        </div>
        <!-- /SIDEBAR -->

        <!-- MAIN CONTENT -->
        <div class="main-content">
          <!-- TABS FOR TAXONOMIES -->
          <div class="tabs-row">
            <span class="tabs-header">Taxonomies:</span>
            <div class="wrapped-tabs">
              <div
                v-for="(taxonomy, i) in formattedTaxonomies"
                :key="taxonomy.filename"
                class="wrapped-tab"
                :class="{
                  'tab-active': selectedTaxonomy === taxonomy.filename,
                }"
                @click="selectedTaxonomy = taxonomy.filename"
              >
                <div
                  v-if="i === 0"
                  style="
                    font-size: 0.7rem;
                    color: #999;
                    margin-bottom: 3px;
                    font-weight: 600;
                  "
                >
                  Primary
                </div>
                {{ taxonomy.displayName }}
              </div>
            </div>
          </div>

          <!-- TABS FOR CATEGORIES (only if attributeManagement or taxonomyBuilder) -->
          <div
            class="tabs-row"
            v-if="
              topLevelNodes.length &&
              (currentView === 'attributeManagement' ||
                currentView === 'taxonomyBuilder')
            "
          >
            <span class="tabs-header">Categories:</span>
            <div class="wrapped-tabs">
              <div
                v-for="node in topLevelNodes"
                :key="node"
                class="wrapped-tab"
                :class="{ 'tab-active': selectedTopLevelNode === node }"
                @click="selectedTopLevelNode = node"
              >
                {{ node }}
              </div>
            </div>
          </div>

          <!-- 1) TAXONOMY EDITOR VIEW -->
          <div
            v-if="currentView === 'attributeManagement'"
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <h2 class="view-title">AlliumAI Taxonomy Editor</h2>
              </div>
              <div class="panes-container">
                <!-- LEFT PANE: Tree -->
                <div class="pane-left" :style="{ width: leftPaneWidth + 'px' }">
                  <div
                    style="
                      padding: 10px;
                      display: flex;
                      gap: 20px;
                      align-items: center;
                    "
                  >
                    <v-switch
                      v-model="horizontal"
                      label="Horizontal Layout"
                      hide-details
                    ></v-switch>
                    <v-switch
                      v-model="collapsable"
                      label="Foldable"
                      hide-details
                    ></v-switch>
                  </div>

                  <div class="tree-scroll-container">
                    <vue3-tree-org
                      :data="orgData"
                      :horizontal="horizontal"
                      :collapsable="collapsable"
                      :label-style="{ color: '#000', background: '#fff' }"
                      :node-draggable="false"
                      :scalable="false"
                      :default-expand-level="1"
                      :define-menus="customMenus"
                      @on-node-click="onNodeClick"
                      @on-contextmenu="onContextMenuAction"
                      @on-expand="onExpandChange"
                    >
                      <!-- Node template with drop handlers added -->
                      <template v-slot="{ node }">
                        <div
                          class="org-tree-node__text node-label custom-node"
                          :style="{
                            color:
                              Array.isArray(node.filters) &&
                              node.filters.length > 0
                                ? '#4caf50'
                                : node.filters &&
                                  typeof node.filters === 'object' &&
                                  Object.keys(node.filters).length > 0
                                ? '#4caf50'
                                : 'inherit',
                            background: node.style?.background || '#fff',
                            borderColor:
                              Array.isArray(node.filters) &&
                              node.filters.length > 0
                                ? '#108ffe'
                                : node.filters &&
                                  typeof node.filters === 'object' &&
                                  Object.keys(node.filters).length > 0
                                ? '#108ffe'
                                : '#ddd',
                          }"
                          @dragover.prevent
                          @drop="onFilterDrop($event, node)"
                        >
                          <div class="custom-content">
                            {{ node.label || node.name }}
                          </div>
                          <!-- Category-level sources as color-coded dots -->
                          <div
                            class="source-chips-row"
                            v-if="nodeDataSources(node).length > 0"
                          >
                            <span
                              v-for="sourceName in nodeDataSources(node)"
                              :key="sourceName"
                              class="source-dot"
                              :style="{
                                backgroundColor:
                                  sourceColors[sourceName] || '#AAA',
                              }"
                              :title="sourceName"
                            />
                          </div>
                        </div>
                      </template>
                    </vue3-tree-org>
                  </div>

                  <!-- Legend for source colors -->
                  <div class="source-legend">
                    <div
                      v-for="(color, sourceName) in sourceColors"
                      :key="sourceName"
                      class="legend-item"
                    >
                      <span
                        class="legend-dot"
                        :style="{ backgroundColor: color }"
                      ></span>
                      {{ sourceName }}
                    </div>
                  </div>
                </div>
                <!-- /LEFT PANE -->

                <!-- Divider -->
                <div class="divider" @mousedown.prevent="startDrag"></div>

                <!-- RIGHT PANE: Filters -->
                <div class="pane-right">
                  <!-- If no node selected -->
                  <template v-if="!selectedNode">
                    <p>Select a node to see attributes</p>
                  </template>

                  <!-- If a node is selected -->
                  <template v-else>
                    <h3 style="margin-bottom: 1rem">
                      {{ selectedNode.name }} Attributes
                    </h3>

                    <!-- Add Existing Filter row -->
                    <div
                      class="add-existing-filter-row"
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        margin-bottom: 1rem;
                      "
                    >
                      <v-autocomplete
                        v-model="addExistingFilterName"
                        :items="allFilterNames"
                        label="Add existing attribute"
                        hide-details
                        clearable
                        placeholder="Type or select attribute"
                        @update:modelValue="onExistingFilterChange"
                        style="max-width: 250px"
                      ></v-autocomplete>
                    </div>

                    <template v-if="showFilters">
                      <v-list dense style="padding: 0">
                        <v-list-item-group>
                          <!-- For each filter name => arrayOfValues in editableFilters -->
                          <div
                            v-for="(values, filterName) in editableFilters"
                            :key="filterName"
                            class="filter-container"
                          >
                            <v-list-item style="padding: 0.5rem">
                              <v-list-item-content
                                style="
                                  display: flex;
                                  align-items: center;
                                  gap: 0.5rem;
                                "
                              >
                                <!-- Filter name editing logic -->
                                <!-- Non-editable filter name chip -->
                                <v-chip
                                  class="filter-name"
                                  draggable="true"
                                  title="Drag Filter to Node to Copy"
                                  @dragstart="
                                    onFilterDragStart($event, filterName)
                                  "
                                >
                                  {{ editableFilterNames[filterName] }}
                                </v-chip>
                                <!-- Filter-level sources as color-coded dots -->
                                <div
                                  class="source-chips-row"
                                  v-if="
                                    getFilterNameSourcesForSelectedNode(
                                      filterName
                                    ).length > 0
                                  "
                                >
                                  <span
                                    v-for="src in getFilterNameSourcesForSelectedNode(
                                      filterName
                                    )"
                                    :key="src"
                                    class="source-dot"
                                    :style="{
                                      backgroundColor:
                                        sourceColors[src] || '#AAA',
                                    }"
                                    :title="src"
                                  />
                                </div>
                              </v-list-item-content>

                              <!-- Icons: expand, rename, delete -->
                              <v-list-item-icon>
                                <v-icon
                                  small
                                  @click="toggleFilter(filterName)"
                                  style="cursor: pointer"
                                >
                                  {{
                                    expandedFilters.includes(filterName)
                                      ? "mdi-chevron-up"
                                      : "mdi-chevron-down"
                                  }}
                                </v-icon>
                              </v-list-item-icon>
                              <v-list-item-icon>
                                <v-icon
                                  small
                                  color="error"
                                  @click="removeFilter(filterName)"
                                  style="cursor: pointer"
                                >
                                  mdi-delete
                                </v-icon>
                              </v-list-item-icon>
                            </v-list-item>

                            <!-- If expanded, show values in a row with color-coded chips -->
                            <div
                              v-if="expandedFilters.includes(filterName)"
                              class="filter-values-container"
                            >
                              <v-simple-table>
                                <thead>
                                  <tr>
                                    <th style="width: 70%">Values</th>
                                    <th style="width: 30%">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td colspan="2">
                                      <div class="values-row">
                                        <div
                                          v-for="(val, idx) in values"
                                          :key="idx"
                                          class="value-item"
                                        >
                                          <v-chip
                                            class="value-chip"
                                            small
                                            :style="
                                              getValueChipStyle(filterName, val)
                                            "
                                          >
                                            {{ val }}
                                            <v-icon
                                              small
                                              class="delete-icon"
                                              @click.stop="
                                                removeFilterValue(
                                                  filterName,
                                                  idx
                                                )
                                              "
                                            >
                                              mdi-close
                                            </v-icon>
                                          </v-chip>
                                        </div>
                                        <!-- Toggle expand/collapse if more than 7 values -->
                                        <div
                                          v-if="values.length > 7"
                                          style="margin-top: 4px"
                                        >
                                          <v-btn
                                            text
                                            small
                                            @click="
                                              toggleExpandValues(
                                                { attribute_id: filterName },
                                                $event
                                              )
                                            "
                                          >
                                            {{
                                              expandedAttributeValues[
                                                filterName
                                              ]
                                                ? "Collapse"
                                                : "Expand"
                                            }}
                                          </v-btn>
                                        </div>
                                        <!-- Add new value to this filter -->
                                        <div class="add-value">
                                          <v-text-field
                                            v-model="
                                              newFilterValues[filterName]
                                            "
                                            hide-details
                                            placeholder="Add value"
                                            style="max-width: 150px"
                                            @keydown.enter="
                                              addFilterValue(filterName)
                                            "
                                          ></v-text-field>
                                          <v-icon
                                            small
                                            @click="addFilterValue(filterName)"
                                            style="cursor: pointer"
                                          >
                                            mdi-plus
                                          </v-icon>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                </tbody>
                              </v-simple-table>
                            </div>
                          </div>
                        </v-list-item-group>
                      </v-list>

                      <!-- "Add Filter" row -->
                      <div
                        class="add-filter-row"
                        style="
                          display: flex;
                          align-items: center;
                          gap: 0.5rem;
                          margin-top: 1rem;
                        "
                      >
                        <v-text-field
                          v-model="newFilterName"
                          hide-details
                          placeholder="New attribute name"
                          style="max-width: 200px"
                          @keydown.enter="addFilter"
                        ></v-text-field>
                        <v-icon
                          small
                          @click="addFilter"
                          style="cursor: pointer"
                        >
                          mdi-plus
                        </v-icon>
                      </div>
                    </template>

                    <template v-else>
                      <p>No Attributes Found</p>
                      <div
                        class="add-filter-row"
                        style="
                          display: flex;
                          align-items: center;
                          gap: 0.5rem;
                          margin-top: 1rem;
                        "
                      >
                        <v-text-field
                          v-model="newFilterName"
                          hide-details
                          placeholder="New attribute name"
                          style="max-width: 200px"
                          @keydown.enter="addFilter"
                        ></v-text-field>
                        <v-icon
                          small
                          @click="addFilter"
                          style="cursor: pointer"
                        >
                          mdi-plus
                        </v-icon>
                      </div>
                    </template>
                  </template>
                </div>
              </div>
              <div
                style="
                  margin-top: 1rem;
                  display: flex;
                  justify-content: flex-end;
                "
              >
                <v-btn
                  color="secondary"
                  @click="exportCategoriesToCSV"
                  style="height: 36px"
                >
                  Download Categories
                </v-btn>
              </div>
            </div>
          </div>
          <!-- /attributeManagement -->

          <!-- 2) ATTRIBUTE OVERVIEW VIEW -->
          <div
            v-else-if="currentView === 'attributeOverview'"
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <h2 class="view-title">Attribute Overview</h2>
              </div>

              <!-- Card wrapping our summary statistics -->
              <v-card class="pa-4 mb-4 elevation-1">
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Total Attributes</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.totalAttributes }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Distinct Names</div>
                    <div class="stat-value">
                      {{
                        Object.keys(attributeOverviewStats.nameCounts).length
                      }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. Attributes per Category</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgAttrsPerCategory }}
                    </div>
                  </v-col>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. Categories per Attribute</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgCatsPerAttribute }}
                    </div>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <div class="stat-label">Avg. # of Values per Attribute</div>
                    <div class="stat-value">
                      {{ attributeOverviewStats.avgValuesPerFilter }}
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Another card for the big chip list of names -->
              <v-card class="pa-4 elevation-1">
                <v-card-title>All Attribute Names (alphabetical)</v-card-title>
                <v-card-text>
                  <div class="chip-container">
                    <!-- We loop over a *sorted* array of [name, count] pairs -->
                    <v-chip
                      v-for="([name, count], index) in sortedNameEntries"
                      :key="index"
                      class="value-chip attr-name-chip"
                      small
                      @click="goToAttributeEditorByName(name)"
                    >
                      <v-icon
                        small
                        class="delete-icon"
                        @click.stop="deleteAttributesByName(name)"
                      >
                        mdi-close
                      </v-icon>
                      {{ name
                      }}<template v-if="count > 1"> ({{ count }})</template>
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </div>
          <!-- /attributeOverview -->

          <!-- 3) ATTRIBUTE EDITOR VIEW -->
          <div
            v-else-if="currentView === 'attributeEditor'"
            :class="{
              'view-content': true,
              'fullscreen-editor': isAttributeEditorFullscreen,
            }"
          >
            <div class="attribute-management">
              <div
                class="sticky-header"
                style="
                  display: flex;
                  flex-direction: column;
                  margin-bottom: 0.5rem;
                "
              >
                <div
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  "
                >
                  <h2 class="view-title">Attribute Editor</h2>
                  <!-- Sorting selector and Fullscreen toggle -->
                  <div style="display: flex; gap: 1rem; align-items: center">
                    <span>Sort by:</span>
                    <v-select
                      v-model="attributeSort"
                      :items="['display_name', 'attribute_id']"
                      hide-details
                      style="width: 160px"
                    ></v-select>
                    <v-btn icon @click="toggleAttributeEditorFullscreen">
                      <v-icon>
                        {{
                          isAttributeEditorFullscreen
                            ? "mdi-fullscreen-exit"
                            : "mdi-fullscreen"
                        }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>

                <!--  buttons row - now part of sticky header recheck categories -->
                <div style="margin-top: 0.75rem; display: flex; gap: 1rem">
                  <v-btn color="primary" @click="openNameDialog">
                    Create New Attribute
                  </v-btn>
                  <v-btn color="secondary" @click="exportAttributesToCSV">
                    Export CSV
                  </v-btn>
                  <v-btn color="secondary" @click="recheckCategories">
                    Recheck Categories
                  </v-btn>
                </div>
              </div>

              <!-- Data Table -->
              <v-data-table
                :headers="attributeTableHeaders"
                :items="filteredAttributes"
                item-key="attribute_id"
                :items-per-page="15"
                class="elevated"
                :footer-props="{
                  itemsPerPageOptions: [15, 25, 50, 100],
                  showFirstLastPage: true,
                  firstIcon: 'mdi-page-first',
                  lastIcon: 'mdi-page-last',
                  prevIcon: 'mdi-chevron-left',
                  nextIcon: 'mdi-chevron-right',
                }"
                fixed-header
                :height="attributeEditorTableHeight"
              >
                <!-- ID -->
                <template #item.attribute_id="{ item }">
                  <span>{{ item.attribute_id }}</span>
                </template>

                <!-- Name -->
                <template #item.name="{ item }">
                  <a @click="openTextEditDialog(item, 'name')">
                    {{ item.name || "(none)" }}
                  </a>
                </template>

                <!-- Display Name -->
                <template #item.display_name="{ item }">
                  <a @click="openTextEditDialog(item, 'display_name')">
                    {{ getDisplayName(item) }}
                  </a>
                </template>

                <!-- Description (wrap text) -->
                <template #item.description="{ item }">
                  <div
                    @click="openTextEditDialog(item, 'description')"
                    :style="{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'break-word',
                      cursor: 'pointer',
                      maxWidth: '280px',
                    }"
                    title="Click to edit"
                  >
                    {{ item.description || "(none)" }}
                  </div>
                </template>

                <!-- Values (new schema) -->
                <template #item.values="{ item }">
                  <draggable
                    tag="div"
                    :list="item.valuesArray"
                    :group="'attrValues-' + item.attribute_id"
                    item-key="value_en"
                    @end="updateItemValues(item)"
                  >
                    <template #item="{ element, index }">
                      <v-chip
                        v-if="shouldDisplayValue(item, index)"
                        class="value-chip"
                        small
                        :style="
                          getAttributeEditorValueChipStyle(
                            item.attribute_id,
                            element.value_en
                          )
                        "
                        @contextmenu.prevent="
                          showValueContextMenu(
                            $event,
                            item.attribute_id,
                            element.value_en
                          )
                        "
                      >
                        {{ element.value_en }}
                        <v-icon
                          small
                          class="delete-icon"
                          @click.stop="removeAttributeValue(item, index)"
                          >mdi-close</v-icon
                        >
                      </v-chip>
                    </template>
                  </draggable>

                  <div v-if="item.valuesArray.length > 7" class="mt-1">
                    <v-btn text small @click="toggleExpandValues(item)">
                      {{
                        expandedAttributeValues[item.attribute_id]
                          ? "collapse"
                          : "expand"
                      }}
                    </v-btn>
                  </div>

                  <div class="d-flex align-center mt-2" style="gap: 4px">
                    <v-text-field
                      v-model="newAttributeValue[item.attribute_id]"
                      hide-details
                      placeholder="add value"
                      style="max-width: 120px"
                      @keydown.enter="addAttributeValue(item)"
                    />
                    <v-icon
                      small
                      @click="addAttributeValue(item)"
                      style="cursor: pointer"
                      >mdi-plus</v-icon
                    >
                  </div>
                </template>

                <!-- NEW ATTRIBUTE GROUP COLUMN -->
                <template #item.attribute_group="{ item }">
                  <a @click="openTextEditDialog(item, 'attribute_group')">
                    {{ item.attribute_group || "(none)" }}
                  </a>
                </template>

                <!-- Data Type -->
                <template #item.data_type="{ item }">
                  <v-select
                    v-model="item.data_type"
                    :items="['integer', 'float', 'text', 'boolean']"
                    hide-details
                    style="width: 140px"
                  ></v-select>
                </template>

                <!-- Booleans (1) -->
                <template #item.booleans_group_1="{ item }">
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      gap: 1rem;
                      flex-wrap: wrap;
                      align-items: flex-start;
                    "
                  >
                    <!-- Always Required -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.always_required"
                        hide-details
                        :color="item.always_required ? 'success' : 'grey'"
                      />
                      <span>Always Required</span>
                    </div>

                    <!-- Is Image Attr -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.is_image_attribute"
                        hide-details
                        :color="item.is_image_attribute ? 'success' : 'grey'"
                      />
                      <span>Is Image Attr</span>
                    </div>

                    <!-- Is Closed List -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.is_closed_list"
                        hide-details
                        :color="item.is_closed_list ? 'success' : 'grey'"
                      />
                      <span>Is Closed List</span>
                    </div>
                  </div>
                </template>

                <!-- Booleans (2) -->
                <template #item.booleans_group_2="{ item }">
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      gap: 1rem;
                      flex-wrap: wrap;
                      align-items: flex-start;
                    "
                  >
                    <!-- Filter Attribute -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.filter_attribute"
                        hide-details
                        :color="item.filter_attribute ? 'success' : 'grey'"
                      />
                      <span>Filter Attribute</span>
                    </div>

                    <!-- Critical Attribute -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.critical_attribute"
                        hide-details
                        :color="item.critical_attribute ? 'success' : 'grey'"
                      />
                      <span>Critical Attribute</span>
                    </div>
                  </div>
                </template>

                <!-- Booleans (3) -->
                <template #item.booleans_group_3="{ item }">
                  <div
                    style="
                      display: flex;
                      flex-direction: column;
                      gap: 1rem;
                      flex-wrap: wrap;
                      align-items: flex-start;
                    "
                  >
                    <!-- Secondary Allowed -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.secondary_allowed"
                        hide-details
                        :color="item.secondary_allowed ? 'success' : 'grey'"
                      />
                      <span>Secondary Allowed</span>
                    </div>

                    <!-- Customer Facing -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.customer_facing"
                        hide-details
                        :color="item.customer_facing ? 'success' : 'grey'"
                      />
                      <span>Customer Facing</span>
                    </div>
                    <!-- Category Node (NEW) -->
                    <div
                      style="
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        flex-wrap: wrap;
                      "
                    >
                      <v-switch
                        v-model="item.is_category_type_attribute"
                        hide-details
                        :color="
                          item.is_category_type_attribute ? 'success' : 'grey'
                        "
                      />
                      <span>Category Type Attr</span>
                    </div>
                  </div>
                </template>

                <!-- Sort Order -->
                <template #item.sort_order="{ item }">
                  <v-select
                    v-model="item.sort_order"
                    :items="[
                      'None',
                      'Alphabetical',
                      'Value Sort',
                      'Custom Sort',
                    ]"
                    hide-details
                    style="width: 140px"
                  ></v-select>
                </template>

                <!-- Attribute Level -->
                <template #item.attribute_level="{ item }">
                  <v-select
                    v-model="item.attribute_level"
                    :items="['Product', 'Variant']"
                    hide-details
                    style="width: 140px"
                  ></v-select>
                </template>

                <!-- Attribute Unit -->
                <template #item.attribute_unit="{ item }">
                  <v-text-field
                    v-model="item.attribute_unit"
                    placeholder="Attribute Unit"
                    hide-details
                    style="width: 120px"
                  />
                </template>

                <!-- Notes (wrap text) -->
                <template #item.notes="{ item }">
                  <div
                    @click="openTextEditDialog(item, 'notes')"
                    :style="{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'break-word',
                      cursor: 'pointer',
                      maxWidth: '280px',
                    }"
                    title="Click to edit"
                  >
                    {{ item.notes || "(none)" }}
                  </div>
                </template>

                <!-- filter_sources: color-coded chips -->
                <template #item.filter_sources="{ item }">
                  <div style="display: flex; flex-wrap: wrap; gap: 0.5rem">
                    <template
                      v-if="item.filter_sources && item.filter_sources.length"
                    >
                      <v-chip
                        v-for="(src, i) in item.filter_sources"
                        :key="i"
                        class="value-chip"
                        small
                        :style="getSourceChipStyle(src)"
                      >
                        {{ src }}
                      </v-chip>
                    </template>
                    <template v-else>
                      <span style="font-size: 0.9rem; color: #666">(none)</span>
                    </template>
                  </div>
                </template>

                <!-- categories: simple chips showing each category path -->
                <template #item.categories="{ item }">
                  <div
                    style="display: flex; flex-direction: column; gap: 0.5rem"
                  >
                    <!-- Category chips container -->
                    <div style="display: flex; flex-wrap: wrap; gap: 0.5rem">
                      <template
                        v-if="item.categories && item.categories.length"
                      >
                        <template v-for="(cat, i) in item.categories">
                          <v-chip
                            v-if="shouldDisplayCategory(item, i)"
                            :key="i"
                            class="value-chip"
                            small
                            color="#e0f7fa"
                            style="color: #000; position: relative"
                          >
                            {{ cat }}
                          </v-chip>
                        </template>
                      </template>
                      <template v-else>
                        <span style="font-size: 0.9rem; color: #666"
                          >(none)</span
                        >
                      </template>
                    </div>

                    <!-- Add expand/collapse button if there are more than 7 categories -->
                    <div
                      v-if="item.categories && item.categories.length > 7"
                      class="mt-1"
                    >
                      <v-btn text small @click="toggleExpandCategories(item)">
                        {{
                          expandedCategories[item.attribute_id]
                            ? "collapse"
                            : "expand"
                        }}
                      </v-btn>
                    </div>
                  </div>
                </template>

                <!-- Actions column with a delete button -->
                <template #item.actions="{ item }">
                  <v-btn icon color="error" @click="deleteAttribute(item)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </div>
          <!-- /attributeEditor -->

          <!-- 4) LANGUAGE VARIANTS VIEW -->
          <div
            v-else-if="currentView === 'languageVariants'"
            class="view-content"
          >
            <div class="attribute-management">
              <div class="sticky-header">
                <h2 class="view-title">Language Variants Editor</h2>
              </div>

              <!-- Search box for language variants specifically -->
              <div style="margin-bottom: 1rem; display: flex; gap: 1rem">
                <v-text-field
                  v-model="langVariantSearch"
                  label="Search by ID or English Display Name"
                  append-icon="mdi-magnify"
                  hide-details
                  clearable
                  style="max-width: 300px"
                ></v-text-field>
              </div>

              <!-- Data table listing all attributes for language variants -->
              <v-data-table
                :headers="langVariantHeaders"
                :items="filteredLanguageVariantAttributes"
                item-key="attribute_id"
                :items-per-page="15"
                class="elevated"
                :footer-props="{ itemsPerPageOptions: [15, 25, 50, 100] }"
                fixed-header
                height="600px"
              >
                <!-- ID -->
                <template #item.attribute_id="{ item }">
                  {{ item.attribute_id }}
                </template>
                <!-- English Display Name -->
                <template #item.display_name="{ item }">
                  {{ getDisplayName(item) }}
                </template>
                <!-- Number of Values -->
                <template #item.value_count="{ item }">
                  {{ getValuesArray(item) ? getValuesArray(item).length : 0 }}
                </template>
                <!-- Action: open translation dialog -->
                <template #item.actions="{ item }">
                  <v-btn
                    small
                    color="primary"
                    @click="openTranslationsDialog(item)"
                  >
                    Edit Translations
                  </v-btn>
                </template>
              </v-data-table>
            </div>
          </div>
          <!-- /languageVariants -->
        </div>
      </div>

      <!-- Snackbar -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        timeout="2000"
        top
        right
      >
        {{ snackbar.message }}
      </v-snackbar>

      <!-- Dialog for renaming node -->
      <v-dialog v-model="editNodeDialog" max-width="500px">
        <v-card>
          <v-card-title> Edit Node Name </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="editNodeName"
              label="Node Name"
            ></v-text-field>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="editNodeDialog = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveNodeName">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog for Publish Taxonomy -->
      <v-dialog v-model="publishDialog" max-width="400px">
        <v-card>
          <v-card-title>Publish Taxonomy</v-card-title>
          <v-card-text>
            <p>
              A backup of the current version will be created before publishing.
            </p>
            <v-btn
              @click="publishTo('production')"
              color="primary"
              style="margin-right: 1rem"
            >
              Publish to Production
            </v-btn>
            <v-btn @click="publishTo('test')" color="secondary">
              Publish to Test
            </v-btn>
          </v-card-text>
          <v-card-actions>
            <v-btn text @click="publishDialog = false">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog for editing text fields in attribute editor -->
      <v-dialog v-model="textEditDialog.show" max-width="500px">
        <v-card>
          <v-card-title> Edit {{ textEditDialog.fieldName }} </v-card-title>
          <v-card-text>
            <v-textarea
              v-model="textEditDialog.tempValue"
              label="Value"
              rows="3"
              auto-grow
              hide-details
            ></v-textarea>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="textEditDialog.show = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveTextEdit">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="nameDialog" max-width="400px">
        <v-card>
          <v-card-title>Enter Attribute Name</v-card-title>
          <v-card-text>
            <v-text-field v-model="newAttributeName" label="Attribute Name" />
          </v-card-text>
          <v-card-actions>
            <v-btn @click="nameDialog = false">Cancel</v-btn>
            <v-btn @click="createAttributeWithName" color="primary"
              >Create</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- notes dialog for individual values -->
      <v-dialog v-model="valueNotesDialog.show" max-width="500px">
        <v-card>
          <v-card-title>
            Notes for "{{ valueNotesDialog.value }}"
          </v-card-title>
          <v-card-text>
            <v-textarea
              v-model="valueNotesDialog.tempNote"
              label="Notes"
              rows="5"
              auto-grow
              hide-details
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="valueNotesDialog.show = false">Cancel</v-btn>
            <v-btn color="primary" text @click="saveValueNotes">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- DIALOG FOR EDITING A SINGLE ATTRIBUTE'S TRANSLATIONS -->
      <v-dialog v-model="translationsDialog.show" max-width="700px" persistent>
        <v-card>
          <v-card-title>
            Translations for Attribute
            {{ translationsDialog.item?.display_name.en }}
          </v-card-title>
          <v-card-text v-if="translationsDialog.item">
            <v-form>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.de"
                    label="Display Name (German)"
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.fr"
                    label="Display Name (French)"
                    dense
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="translationsDialog.item.display_name.it"
                    label="Display Name (Italian)"
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-divider class="my-3"></v-divider>
              <div
                v-if="
                  translationsDialog.item.valuesArray &&
                  translationsDialog.item.valuesArray.length
                "
              >
                <h3 style="margin-bottom: 8px">Value Translations</h3>
                <div
                  v-for="vo in translationsDialog.item.valuesArray"
                  :key="vo.value_en"
                  class="d-flex align-center mb-2"
                >
                  <div style="width: 30%; font-weight: bold">
                    {{ vo.value_en }}
                  </div>
                  <v-text-field
                    v-model="vo.value_de"
                    label="German"
                    dense
                    style="width: 22%; margin-right: 4px"
                  />
                  <v-text-field
                    v-model="vo.value_fr"
                    label="French"
                    dense
                    style="width: 22%; margin-right: 4px"
                  />
                  <v-text-field
                    v-model="vo.value_it"
                    label="Italian"
                    dense
                    style="width: 22%"
                  />
                </div>
              </div>
              <div v-else>
                <p>No values to translate.</p>
              </div>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="error" @click="translationsDialog.show = false"
              >Cancel</v-btn
            >
            <v-btn color="primary" @click="closeTranslationsDialog"
              >Save Translations</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- /SINGLE ATTRIBUTE TRANSLATIONS DIALOG -->
    </div>
    <!-- Value Context Menu -->
    <div
      v-if="valueContextMenu.show"
      class="value-context-menu"
      :style="{
        top: valueContextMenu.y + 'px',
        left: valueContextMenu.x + 'px',
      }"
    >
      <div class="context-menu-item" @click="renameValueFromContextMenu">
        <v-icon small class="mr-2">mdi-pencil</v-icon>
        Rename Value
      </div>
      <div class="context-menu-item" @click="addNotesFromContextMenu">
        <v-icon small class="mr-2">mdi-note-text</v-icon>
        Add/Edit Notes
      </div>
    </div>
  </v-app>
</template>

<script>
import componentOptions from './TaxonomyEditor.js';
export default {  
  ...componentOptions,
};
</script>

<style scoped>
@import "./TaxonomyEditor.css";
</style>


